import { useEffect, useMemo, useState } from 'react';

import { toastService } from '@thefarmersfront/kitchen';

import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler, useStableCallback } from '~/hooks/common';
import { renderFilterErrorMessage, transformToSelectOptions } from '~/utils';

import { API_ENDPOINT, STOCK_CODES, STOCK_CODE_KEY, TypeStockOptionCodes, TypeStockOptions } from '~/constants';

import { TypeGetCodesParams, TypeGetCodesResponse, TypeGetVendorReleaseMethodCodesResponse } from '~/types/stock.types';

const useOption = ({ codes }: { codes: TypeStockOptionCodes[] }) => {
  const { runAllSettled } = useAsyncHandler();

  const [options, setOptions] = useState<TypeStockOptions | null>(null);
  const isRequestVendorReleaseMethod = useMemo(() => codes.includes(STOCK_CODES.vendorReleaseMethod), [codes]);

  const getCodes = (codes: TypeStockOptionCodes[]) =>
    codes.filter((optionCode) => Object.keys(STOCK_CODE_KEY).includes(optionCode));

  const formatCodes = (data: Partial<TypeGetCodesResponse['data']>): TypeStockOptions => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        STOCK_CODE_KEY[key as TypeStockOptionCodes],
        transformToSelectOptions(value, 'code', 'name'),
      ])
    ) as TypeStockOptions;
  };

  const getOptionCodes = useAPI<TypeGetCodesParams, TypeGetCodesResponse>({
    endpoint: API_ENDPOINT.STOCK.GET_CODES,
    method: 'get',
  });

  const getVendorReleaseMethodCodes = useAPI<TypeGetCodesParams, TypeGetVendorReleaseMethodCodesResponse>({
    endpoint: API_ENDPOINT.STOCK.GET_VENDOR_RELEASE_METHOD_CODE,
    method: 'get',
  });

  const requestOptions = useStableCallback(
    async (
      codes: string,
      {
        onSuccess,
      }: {
        onSuccess?: (data: TypeStockOptions) => void;
      } = {}
    ) => {
      const asyncFunctions: [
        () => Promise<TypeGetCodesResponse>,
        ...(() => Promise<TypeGetVendorReleaseMethodCodesResponse>)[],
      ] = [
        () => getOptionCodes({ params: { codes } }),
        ...(isRequestVendorReleaseMethod ? [() => getVendorReleaseMethodCodes({})] : []),
      ];

      runAllSettled(asyncFunctions, {
        onSuccess: (results) => {
          const [codesResult, methodCodeResult] = results;
          const isSuccessCodes = codesResult.status === 'fulfilled';
          const isSuccessMethodCodes = methodCodeResult?.status === 'fulfilled';

          const resultOptions = {
            ...(isSuccessCodes ? codesResult.value.data : {}),
            ...(isSuccessMethodCodes ? methodCodeResult.value.data : {}),
          };

          // 일부 코드 요청이 실패하는 경우에도 에러 처리
          if (!isSuccessCodes || !(isRequestVendorReleaseMethod ? isSuccessMethodCodes : true)) {
            toastService.addToast({
              type: 'error',
              message: renderFilterErrorMessage(),
            });
            return;
          }

          const options = formatCodes(resultOptions);
          setOptions(options);
          onSuccess?.(options);
        },
        onError: () => {
          toastService.addToast({
            type: 'error',
            message: renderFilterErrorMessage(),
          });
        },
      });
    }
  );

  const codesParam = getCodes(codes).join(',');

  useEffect(() => {
    requestOptions(codesParam);
  }, [codesParam, requestOptions]);

  return {
    options,
    requestOptions,
  };
};

export default useOption;

const STOCK_CODE_ITEMS: TypeGetCodesResponse['data'] = {
  VENDOR_RELEASE_CLUSTER: [
    { code: 'CC02', name: '김포' },
    { code: 'CC03', name: '평택' },
  ],
  TEMPERATURE_TYPE: [
    { code: 'ROOM', name: '상온' },
    { code: 'COLD', name: '냉장' },
    { code: 'FROZEN', name: '냉동' },
  ],
  VENDOR_RELEASE_KEYWORD: [
    { code: 'SKU_CODE', name: 'SKU코드' },
    { code: 'SKU_NAME', name: '상품명' },
    { code: 'ALTER_CODE', name: '상품 바코드' },
    { code: 'ID', name: '반출요청번호' },
  ],
  VENDOR_RELEASE_STATUS: [
    { code: 'REQUEST', name: '요청' },
    { code: 'PROGRESS', name: '진행' },
    { code: 'COMPLETE', name: '완료' },
    { code: 'CANCEL', name: '취소' },
  ],
  VENDOR_RELEASE_METHOD: [
    { code: 'PICKUP', name: '방문수령' },
    { code: 'TRUCK', name: '화물' },
    { code: 'PARCEL', name: '택배' },
  ],
  VENDOR_RELEASE_PALLET_TYPE: [
    { code: 'KPP', name: 'KPP' },
    { code: 'AJ', name: 'AJ' },
  ],
  VENDOR_RELEASE_PACKING_METHOD: [
    { code: 'BOX', name: '박스' },
    { code: 'PALLET', name: '파렛트' },
  ],
  VENDOR_RELEASE_INVENTORY_KEYWORD: [
    { code: 'SKU_CODE', name: 'SKU코드' },
    { code: 'SKU_NAME', name: '상품명' },
    { code: 'ALTER_CODE', name: '상품 바코드' },
  ],
};

const isStockOptionCode = (code: string): code is TypeStockOptionCodes => code in STOCK_CODE_ITEMS;

// 화면마다 요청하는 코드 묶음이 달라, 요청한 코드만 골라 응답한다.
registerMockAPI<{ data: Partial<TypeGetCodesResponse['data']> }>({
  method: 'get',
  endpoint: API_ENDPOINT.STOCK.GET_CODES,
  label: ['재고/반출요청관리/공통코드', '재고/반출요청상세/공통코드', '재고/반출요청등록/공통코드'],
  scenarios: [
    {
      name: '정상 응답',
      response: ({ searchParams }) => {
        const requested = (searchParams.get('codes') ?? '').split(',').filter(isStockOptionCode);
        return { data: Object.fromEntries(requested.map((code) => [code, STOCK_CODE_ITEMS[code]])) };
      },
    },
  ],
});

registerMockAPI<TypeGetVendorReleaseMethodCodesResponse>({
  method: 'get',
  endpoint: API_ENDPOINT.STOCK.GET_VENDOR_RELEASE_METHOD_CODE,
  label: ['재고/반출요청관리/반출방식코드', '재고/반출요청상세/반출방식코드'],
  scenarios: [
    { name: '정상 응답', response: { data: { VENDOR_RELEASE_METHOD: STOCK_CODE_ITEMS.VENDOR_RELEASE_METHOD } } },
  ],
});
